/**
 * DecisionFlow — PlanComponentReviewPage
 * ui/src/components/admin/PlanComponentReviewPage.jsx
 *
 * Full-page CA-2 review surface for first-time plan component approval (CHANGE-15).
 * Mounted by CompensationPoliciesWorkspace when reviewComponentId URL param is present.
 *
 * Spec authority: comp_policies_ui_spec_s736.md CHANGE-15
 *
 * CRITICAL: useAuth() called directly here — NOT passed as prop from CPW (AD-213, L-132,
 * Rule 23). CompensationPoliciesWorkspace has no top-level useAuth() call (confirmed line
 * 3182 — first hook is useSearchParams). Child components call useAuth() directly.
 *
 * SoD field: component.created_by (plan_component_router.py line 682).
 * plan_component has NO submitted_by column — DDL r57 confirmed (Rule 7, Rule 18).
 *
 * Props:
 *   planComponentId {string|number} — the component to review
 *   onClose         {function}      — called after approve/return, returns CA to prior view
 *
 * API:
 *   GET  /api/v1/plan-components/{id}  — component detail + modifiers
 *   GET  /api/v1/plans/{plan_id}       — parent plan context
 *   POST /api/v1/plan-components/{id}/approve   — approve component
 *   POST /api/v1/plans/{plan_id}/reject         — return plan for changes
 *
 * CSS: rgb(var(--color-*)) tokens only. Zero hardcoded colors.
 */

import { useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthContext.jsx";
import { AlertTriangle, CheckCircle2, ChevronLeft, Clock, User, X } from "lucide-react";

const API_BASE = "/api/v1";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function fmtCurrency(val, code) {
  if (val == null) return "—";
  return `${code ?? ""}$${Number(val).toLocaleString()}`.trim();
}

function fmtPct(val) {
  if (val == null) return "—";
  return `${Math.round(Number(val) * 100)}%`;
}

// ---------------------------------------------------------------------------
// FieldRow — label / value pair
// ---------------------------------------------------------------------------
function FieldRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <span className="shrink-0 text-tok-xs text-[rgb(var(--color-text-muted))]">{label}</span>
      <span className="text-right text-tok-sm font-medium text-[rgb(var(--color-text))]">
        {value ?? "—"}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ErrorNote
// ---------------------------------------------------------------------------
function ErrorNote({ message }) {
  return (
    <div className="rounded-xl border border-[rgb(var(--color-danger))]/20 bg-[rgb(var(--color-danger))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
      {message ?? "Unable to load data. Please refresh."}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modifier status display label
// ---------------------------------------------------------------------------
const MODIFIER_DISPLAY_LABEL = {
  "P3B-TXN":      "Transaction Commission",
  "P3B-REVTYPE":  "Revenue Type Rules",
  "P3B-SPLIT":    "Deal Splits",
  "P3B-SPIFF":    "SPIFF / Tactical",
  "P3B-KICKER":   "Kicker / Multiplier",
  "P3B-CAP":      "Accelerator Cap",
  "P3B-CLAWBACK": "Clawback Policy",
  "P3B-DRAW":     "Draw / Guarantee",
  "P3B-OVERLAY":  "Overlay Attribution",
};

const STATUS_COLOR = {
  approved:         "text-[rgb(var(--color-success))]",
  pending_approval: "text-[rgb(var(--color-info))]",
  draft:            "text-[rgb(var(--color-warning))]",
  rejected:         "text-[rgb(var(--color-danger))]",
};

// ---------------------------------------------------------------------------
// PlanComponentReviewPage
// ---------------------------------------------------------------------------
export default function PlanComponentReviewPage({ planComponentId, onClose }) {
  // AD-213, Rule 23: useAuth() called directly — never passed as prop from CPW.
  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.user_account_id ?? null;

  // Component state
  const [component,    setComponent]    = useState(null);
  const [compLoading,  setCompLoading]  = useState(true);
  const [compError,    setCompError]    = useState(null);

  // Parent plan state
  const [plan,         setPlan]         = useState(null);
  const [planLoading,  setPlanLoading]  = useState(false);
  const [planError,    setPlanError]    = useState(null);

  // Action state
  const [actioning,    setActioning]    = useState(false);
  const [actionError,  setActionError]  = useState("");
  const [actionSuccess,setActionSuccess]= useState("");

  // Return for changes state
  const [showReturnForm,  setShowReturnForm]  = useState(false);
  const [returnReason,    setReturnReason]    = useState("");

  // Load component
  useEffect(() => {
    if (!planComponentId) return;
    setCompLoading(true);
    setCompError(null);
    fetch(`${API_BASE}/plan-components/${planComponentId}`, { credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(setComponent)
      .catch((e) => setCompError(e.message ?? "Unable to load component."))
      .finally(() => setCompLoading(false));
  }, [planComponentId]);

  // Load parent plan once component is available
  useEffect(() => {
    if (!component?.plan_id) return;
    setPlanLoading(true);
    setPlanError(null);
    fetch(`${API_BASE}/plans/${component.plan_id}`, { credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(setPlan)
      .catch((e) => setPlanError(e.message ?? "Unable to load parent plan."))
      .finally(() => setPlanLoading(false));
  }, [component?.plan_id]);

  // SoD check: created_by (Rule 18 — confirmed plan_component_router.py line 682)
  // plan_component has NO submitted_by column (DDL r57, Rule 7).
  const isSelfApproval = !!(
    currentUserId &&
    component?.created_by &&
    String(currentUserId) === String(component.created_by)
  );

  const isAlreadyApproved = !!(component?.approved_by || component?.approved_at);

  // ---------------------------------------------------------------------------
  // Approve handler
  // ---------------------------------------------------------------------------
  async function handleApprove() {
    if (isSelfApproval) return;
    setActioning(true);
    setActionError("");
    try {
      const res = await fetch(`${API_BASE}/plan-components/${planComponentId}/approve`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.detail?.error?.message ?? b?.error?.message ?? `Error ${res.status}`);
      }
      setActionSuccess("Component approved. The plan will reflect approval status.");
      setTimeout(() => onClose(), 1500);
    } catch (e) {
      setActionError(e.message ?? "Unable to approve component.");
    } finally {
      setActioning(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Return for changes handler — rejects the parent plan (POST /plans/{id}/reject)
  // ---------------------------------------------------------------------------
  async function handleReturnForChanges() {
    if (!returnReason.trim()) { setActionError("Return reason is required."); return; }
    if (!component?.plan_id) { setActionError("No parent plan found."); return; }
    setActioning(true);
    setActionError("");
    try {
      const res = await fetch(`${API_BASE}/plans/${component.plan_id}/reject`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rejection_reason: returnReason.trim() }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.detail?.error?.message ?? b?.error?.message ?? `Error ${res.status}`);
      }
      setActionSuccess("Plan returned for changes. CA-1 will be notified.");
      setTimeout(() => onClose(), 1500);
    } catch (e) {
      setActionError(e.message ?? "Unable to return plan for changes.");
    } finally {
      setActioning(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-[rgb(var(--color-surface-muted))]">

      {/* ── Top bar ── */}
      <div className="sticky top-0 z-40 flex items-center justify-between border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-6 py-4 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-1.5 text-tok-sm text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>
          <span className="text-[rgb(var(--color-border))]">·</span>
          <div>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">CA-2 Component Review</p>
            <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
              {plan?.plan_name ?? (component?.plan_id ? `Plan ${component.plan_id}` : "Loading…")}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-2 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] hover:text-[rgb(var(--color-text))]"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* ── Content ── */}
      <div className="mx-auto max-w-3xl px-6 py-8 space-y-6">

        {compLoading && (
          <div className="animate-pulse space-y-3">
            <div className="h-8 w-1/3 rounded-xl bg-[rgb(var(--color-surface-muted))]" />
            <div className="h-32 rounded-xl bg-[rgb(var(--color-surface-muted))]" />
          </div>
        )}

        {compError && <ErrorNote message={compError} />}

        {!compLoading && !compError && component && (
          <>
            {/* SoD warning */}
            {isSelfApproval && (
              <div className="flex items-start gap-2 rounded-lg border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/10 px-4 py-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-warning))]" />
                <p className="text-tok-sm text-[rgb(var(--color-warning))]">
                  You created this component — a second Compensation Administrator must approve it (NN-36 separation of duties).
                </p>
              </div>
            )}

            {/* Already approved notice */}
            {isAlreadyApproved && (
              <div className="flex items-start gap-2 rounded-lg border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-4 py-3">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--color-success))]" />
                <p className="text-tok-sm text-[rgb(var(--color-success))]">
                  This component is already approved.
                </p>
              </div>
            )}

            {/* ── Plan Context ── */}
            {(planLoading || plan) && (
              <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
                <div className="border-b border-[rgb(var(--color-border))] px-4 py-3">
                  <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
                    Parent Plan
                  </p>
                </div>
                {planLoading && (
                  <div className="px-4 py-4 animate-pulse">
                    <div className="h-4 w-1/2 rounded bg-[rgb(var(--color-surface-muted))]" />
                  </div>
                )}
                {planError && <div className="px-4 py-3"><ErrorNote message={planError} /></div>}
                {!planLoading && !planError && plan && (
                  <div className="divide-y divide-[rgb(var(--color-border))] px-4">
                    <FieldRow label="Plan Name"   value={plan.plan_name ?? plan.plan_code} />
                    <FieldRow label="Status"      value={plan.plan_status?.replace(/_/g, " ")} />
                    <FieldRow label="Version"     value={plan.version != null ? `v${plan.version}` : null} />
                    <FieldRow label="Period"      value={plan.fiscal_period_id ? `Period ${plan.fiscal_period_id}` : null} />
                    {plan.submitted_by && (
                      <FieldRow label="Submitted by" value={
                        <span className="flex items-center gap-1 justify-end">
                          <User className="h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
                          {String(plan.submitted_by).slice(0, 8)}…
                        </span>
                      } />
                    )}
                    {plan.submitted_at && (
                      <FieldRow label="Submitted at" value={
                        <span className="flex items-center gap-1 justify-end">
                          <Clock className="h-3.5 w-3.5 text-[rgb(var(--color-text-muted))]" />
                          {new Date(plan.submitted_at).toLocaleString()}
                        </span>
                      } />
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── Component Detail ── */}
            <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
              <div className="border-b border-[rgb(var(--color-border))] px-4 py-3">
                <div className="flex items-center justify-between">
                  <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
                    Component #{planComponentId}
                  </p>
                  <span className={[
                    "inline-flex items-center rounded-full px-2.5 py-0.5 text-tok-xs font-medium",
                    isAlreadyApproved
                      ? "bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]"
                      : "bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))]",
                  ].join(" ")}>
                    {isAlreadyApproved ? "Approved" : "Pending Approval"}
                  </span>
                </div>
              </div>
              <div className="divide-y divide-[rgb(var(--color-border))] px-4">
                <FieldRow label="OTE Min"      value={fmtCurrency(component.ote_min, component.ote_currency_code)} />
                <FieldRow label="OTE Midpoint" value={fmtCurrency(component.ote_midpoint, component.ote_currency_code)} />
                <FieldRow label="OTE Max"      value={fmtCurrency(component.ote_max, component.ote_currency_code)} />
                <FieldRow label="Pay Mix"      value={
                  component.base_pct != null && component.variable_pct != null
                    ? `${fmtPct(component.base_pct)} base / ${fmtPct(component.variable_pct)} variable`
                    : null
                } />
                <FieldRow label="ARR Weight"      value={fmtPct(component.arr_weight)} />
                {Number(component.mbo_weight ?? 0) > 0 && (
                  <FieldRow label="MBO Weight" value={fmtPct(component.mbo_weight)} />
                )}
                <FieldRow label="Effective Date" value={component.effective_date ?? null} />
                <FieldRow label="Created by"     value={
                  component.created_by
                    ? <span className="font-mono text-tok-xs">{String(component.created_by).slice(0, 8)}…</span>
                    : null
                } />
              </div>
            </div>

            {/* ── Modifier Applicability ── */}
            {component.modifiers && component.modifiers.length > 0 && (
              <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]">
                <div className="border-b border-[rgb(var(--color-border))] px-4 py-3">
                  <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
                    Modifiers
                  </p>
                </div>
                <div className="divide-y divide-[rgb(var(--color-border))]">
                  {component.modifiers.map((m) => (
                    <div key={m.applicability_id ?? m.modifier_type}
                         className="flex items-center justify-between gap-3 px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="text-tok-sm text-[rgb(var(--color-text))]">
                          {MODIFIER_DISPLAY_LABEL[m.modifier_type] ?? m.modifier_type}
                        </span>
                        <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                          · {m.applicability}
                        </span>
                      </div>
                      <span className={`text-tok-xs font-medium ${STATUS_COLOR[m.modifier_status] ?? "text-[rgb(var(--color-text-muted))]"}`}>
                        {m.modifier_status ?? "not configured"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Success / Error feedback ── */}
            {actionSuccess && (
              <div className="rounded-lg border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-success))]">
                {actionSuccess}
              </div>
            )}
            {actionError && <ErrorNote message={actionError} />}

            {/* ── Return for Changes form ── */}
            {showReturnForm && (
              <div className="rounded-xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-surface))] px-4 py-4 space-y-3">
                <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
                  Return for Changes
                </p>
                <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                  Provide a reason. The plan will return to draft and CA-1 will be notified.
                </p>
                <textarea
                  value={returnReason}
                  onChange={(e) => setReturnReason(e.target.value)}
                  placeholder="Describe what needs to be corrected…"
                  rows={3}
                  className="w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] placeholder:italic placeholder:text-[rgb(var(--color-placeholder))] focus:border-[rgb(var(--color-primary))] focus:outline-none resize-none"
                />
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleReturnForChanges}
                    disabled={actioning || !returnReason.trim()}
                    className="rounded-lg border border-[rgb(var(--color-danger))]/40 bg-[rgb(var(--color-danger))]/5 px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-danger))] hover:bg-[rgb(var(--color-danger))]/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actioning ? "Returning…" : "Confirm Return"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowReturnForm(false); setReturnReason(""); setActionError(""); }}
                    className="text-tok-sm text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* ── Action buttons ── */}
            {!actionSuccess && !isAlreadyApproved && !showReturnForm && (
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={handleApprove}
                  disabled={actioning || isSelfApproval}
                  className="rounded-lg bg-[rgb(var(--color-primary))] px-5 py-2.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actioning ? "Approving…" : "Approve Component"}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowReturnForm(true); setActionError(""); }}
                  disabled={actioning}
                  className="rounded-lg border border-[rgb(var(--color-danger))]/40 bg-[rgb(var(--color-danger))]/5 px-5 py-2.5 text-tok-sm font-medium text-[rgb(var(--color-danger))] hover:bg-[rgb(var(--color-danger))]/10 disabled:opacity-50"
                >
                  Return for Changes
                </button>
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
}
